import React, { useEffect, useState } from 'react';
import { BriefcaseBusiness, Building2, Plus, Save, Trash2, Users } from 'lucide-react';
import { api } from '../api.js';
import { Badge, Button, Callout, PageHeader, SectionHeader } from '../components/ui.jsx';
import { classNames } from '../utils.js';

const EMPTY_DEPARTMENT = { name: '', description: '', active: true };

export default function DepartmentsPage({ state, refresh, notify }) {
  const departments = state?.departments || [];
  const [editingId, setEditingId] = useState(departments[0]?.id || 'new');
  const [draft, setDraft] = useState(departments[0] || EMPTY_DEPARTMENT);
  const [saving, setSaving] = useState(false);
  const selectedVersion = departments.find((item) => item.id === editingId)?.updatedAt || '';

  useEffect(() => {
    if (editingId === 'new') return;
    const department = departments.find((item) => item.id === editingId);
    if (department) setDraft({ ...department });
  }, [editingId, selectedVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const createNew = () => { setEditingId('new'); setDraft({ ...EMPTY_DEPARTMENT }); };
  const save = async () => {
    if (!draft.name.trim()) return notify('请填写部门名称', 'danger');
    setSaving(true);
    try {
      const saved = editingId === 'new'
        ? await api.createDepartment(draft)
        : await api.updateDepartment(editingId, draft);
      setEditingId(saved.id);
      notify(editingId === 'new' ? '部门已创建' : '部门信息已保存');
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (editingId === 'new' || !window.confirm(`删除部门“${draft.name}”？`)) return;
    try {
      await api.deleteDepartment(editingId);
      notify('部门已删除');
      const remaining = departments.filter((item) => item.id !== editingId);
      setEditingId(remaining[0]?.id || 'new');
      setDraft(remaining[0] || { ...EMPTY_DEPARTMENT });
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
  };

  return (
    <div className="page departments-page">
      <PageHeader eyebrow="组织结构" title="部门管理" description="部门用于岗位归类和招聘责任分工" actions={<Button variant="primary" icon={Plus} onClick={createNew}>新建部门</Button>} />
      <div className="departments-layout">
        <aside className="department-list-panel">
          <div className="department-list-head"><strong>全部部门</strong><Badge>{departments.length}</Badge></div>
          <div className="department-list">{departments.map((department) => (
            <button key={department.id} className={classNames(editingId === department.id && 'active')} onClick={() => setEditingId(department.id)}>
              <span><Building2 size={17} /></span>
              <div><strong>{department.name}</strong><small>{department.jobCount || 0} 个岗位 · {department.userCount || 0} 位成员</small></div>
              <Badge tone={department.active ? 'success' : 'neutral'}>{department.active ? '启用' : '停用'}</Badge>
            </button>
          ))}</div>
        </aside>
        <section className="department-editor panel">
          <header><div><span>{editingId === 'new' ? '新部门' : '部门设置'}</span><h2>{draft.name || '未命名部门'}</h2></div><div>{editingId !== 'new' && <Button variant="danger-ghost" icon={Trash2} onClick={remove}>删除</Button>}<Button variant="primary" icon={Save} loading={saving} onClick={save}>保存</Button></div></header>
          <div className="department-editor-body">
            <SectionHeader title="基本信息" description="修改部门名称会同步更新所属岗位" />
            <div className="form-grid">
              <label><span>部门名称 *</span><input value={draft.name || ''} onChange={(event) => update('name', event.target.value)} placeholder="例如：品牌与电商增长" /></label>
              <label><span>部门说明</span><textarea rows="5" value={draft.description || ''} onChange={(event) => update('description', event.target.value)} placeholder="说明该部门负责的业务范围和常见岗位" /></label>
            </div>
            <label className="check-row"><input type="checkbox" checked={Boolean(draft.active)} onChange={(event) => update('active', event.target.checked)} />允许新岗位归属到该部门</label>
            <div className="department-stats">
              <div><BriefcaseBusiness size={18} /><span><strong>{draft.jobCount || 0}</strong><small>岗位</small></span></div>
              <div><Users size={18} /><span><strong>{draft.userCount || 0}</strong><small>授权账户</small></span></div>
            </div>
            <Callout tone="info" title="数据范围规则">管理员与人事可查看全部部门；招聘官只能查看和管理已授权负责部门的岗位、候选人与简历。</Callout>
          </div>
        </section>
      </div>
    </div>
  );
}
